import { Box, Tooltip, Stack, Typography, useTheme } from "@mui/material";
import * as React from "react";

interface Trip {
  tripId: string;
  start: Date;
  end: Date;
  locationId: string;
  locationType: 'hangar' | 'ramp';
  locationName?: string;
  status?: 'scheduled' | 'active' | 'completed' | 'cancelled';
}

interface Props {
  trips: Trip[];
  rangeStart: Date;
  rangeEnd: Date;
  currentTime: Date;
  currentLocationId: string;
  onTimeChange?: (time: Date) => void;
}

export const TripIndicator: React.FC<Props> = ({
  trips,
  rangeStart,
  rangeEnd,
  currentTime,
  currentLocationId,
  onTimeChange,
}) => {
  const theme = useTheme();

  // Calculate timeline segments
  const segments = React.useMemo(() => {
    const rangeStartTime = rangeStart.getTime();
    const rangeEndTime = rangeEnd.getTime();
    const rangeDuration = rangeEndTime - rangeStartTime;
    const currentTimeMs = currentTime.getTime();

    return trips
      .map(trip => {
        const tripStart = trip.start.getTime();
        const tripEnd = trip.end.getTime();

        // Skip trips completely outside the range
        if (tripEnd < rangeStartTime || tripStart > rangeEndTime) {
          return null;
        }

        // Calculate position and width as percentages
        const startPos = Math.max(0, ((tripStart - rangeStartTime) / rangeDuration) * 100);
        const endPos = Math.min(100, ((tripEnd - rangeStartTime) / rangeDuration) * 100);
        const width = endPos - startPos;

        // Determine color based on location and time
        const isAtThisLocation = trip.locationId === currentLocationId;
        const isCurrentlyActive = tripStart <= currentTimeMs && tripEnd >= currentTimeMs;
        const isCancelled = trip.status === 'cancelled';

        let color: string;
        if (isCancelled) {
          color = 'rgba(244, 67, 54, 0.7)'; // Red for cancelled
        } else if (isCurrentlyActive && isAtThisLocation) {
          color = theme.palette.success.main; // Green for currently here
        } else if (isAtThisLocation) {
          color = theme.palette.primary.main; // Blue for scheduled here
        } else {
          color = theme.palette.grey[400]; // Gray for other locations
        }

        return {
          tripId: trip.tripId,
          left: `${startPos}%`,
          width: `${width}%`,
          color,
          isActive: isCurrentlyActive,
          locationName: trip.locationName || `${trip.locationType} ${trip.locationId}`,
          start: trip.start,
          end: trip.end,
        };
      })
      .filter((segment): segment is NonNullable<typeof segment> => segment !== null);
  }, [trips, rangeStart, rangeEnd, currentTime, currentLocationId, theme]);

  // Calculate current time marker position
  const currentTimePosition = React.useMemo(() => {
    const rangeStartTime = rangeStart.getTime();
    const rangeEndTime = rangeEnd.getTime();
    const currentTimeMs = currentTime.getTime();

    if (currentTimeMs < rangeStartTime || currentTimeMs > rangeEndTime) {
      return null;
    }

    const position = ((currentTimeMs - rangeStartTime) / (rangeEndTime - rangeStartTime)) * 100;
    return `${position}%`;
  }, [rangeStart, rangeEnd, currentTime]);

  // Format dates for tooltip
  const formatDate = (date: Date) => {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: 20,
        bgcolor: theme.palette.grey[100],
        borderRadius: 0.5,
        overflow: 'hidden',
      }}
    >
      {/* Background track */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: 4,
          bgcolor: theme.palette.grey[200],
          transform: 'translateY(-50%)',
        }}
      />

      {/* Trip segments */}
      {segments.map((segment, index) => (
        <Tooltip
          key={`${segment.tripId}-${index}`}
          title={
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {segment.locationName}
              </Typography>
              <Typography variant="caption">
                {formatDate(segment.start)} - {formatDate(segment.end)}
              </Typography>
            </Stack>
          }
          placement="top"
          arrow
        >
          <Box
            onClick={() => onTimeChange?.(segment.start)}
            sx={{
              position: 'absolute',
              top: '50%',
              left: segment.left,
              width: segment.width,
              height: segment.isActive ? 10 : 8,
              bgcolor: segment.color,
              transform: 'translateY(-50%)',
              borderRadius: 0.25,
              transition: 'all 0.2s',
              cursor: 'pointer',
              '&:hover': {
                height: 12,
                zIndex: 10,
                boxShadow: 1,
              },
              ...(segment.isActive && {
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': {
                    opacity: 1,
                  },
                  '50%': {
                    opacity: 0.7,
                  },
                  '100%': {
                    opacity: 1,
                  },
                },
              }),
            }}
          />
        </Tooltip>
      ))}

      {/* Current time marker */}
      {currentTimePosition && (
        <Box
          sx={{
            position: 'absolute',
            left: currentTimePosition,
            top: 0,
            bottom: 0,
            width: 2,
            bgcolor: theme.palette.error.main,
            zIndex: 20,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: theme.palette.error.main,
              transform: 'translate(-50%, -50%)',
            },
          }}
        />
      )}
    </Box>
  );
};